import React, { createContext, useContext, useState, useEffect } from 'react';
import { safeGetItem, safeSetItem } from '../utils/storage';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [themeMode, setThemeMode] = useState('system'); // 'light', 'dark', 'system'

  // Определяем системную тему
  const getSystemTheme = () => {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  };

  // Загружаем сохраненную тему при инициализации
  useEffect(() => {
    const savedTheme = safeGetItem('minibank-theme');
    if (savedTheme && ['light', 'dark', 'system'].includes(savedTheme)) {
      setThemeMode(savedTheme);
      
      if (savedTheme === 'system') {
        setIsDarkMode(getSystemTheme());
      } else {
        setIsDarkMode(savedTheme === 'dark');
      }
    } else {
      // Если нет сохраненной темы, используем системную
      setThemeMode('system');
      setIsDarkMode(getSystemTheme());
    }
  }, []);

  // Слушаем изменения системной темы
  useEffect(() => {
    if (themeMode === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      
      const handleChange = (e) => {
        setIsDarkMode(e.matches);
      };
      
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [themeMode]);

  // Функция переключения темы (только light <-> dark)
  const toggleTheme = () => {
    const newThemeMode = isDarkMode ? 'light' : 'dark';
    setThemeMode(newThemeMode);
    setIsDarkMode(!isDarkMode);
    safeSetItem('minibank-theme', newThemeMode);
    
    console.log('[ThemeContext] Theme changed to:', newThemeMode);
  };

  // Функция для установки конкретной темы
  const setTheme = (mode) => {
    if (!['light', 'dark', 'system'].includes(mode)) return;
    
    setThemeMode(mode);
    safeSetItem('minibank-theme', mode);
    
    if (mode === 'system') {
      setIsDarkMode(getSystemTheme());
    } else {
      setIsDarkMode(mode === 'dark');
    }
  };

  const value = {
    isDarkMode,
    themeMode,
    toggleTheme,
    setTheme
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}; 